import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import usePublicSettings from '../../hooks/usePublicSettings';
import { cldImg } from '../../utils/cloudinary';

const FALLBACK_WHATSAPP = '917200252350';
const FALLBACK_INSTAGRAM = 'https://www.instagram.com/theyellove.in?igsh=OGRyZ2k3ZWVwMWV1';
const FALLBACK_FACEBOOK = 'https://www.facebook.com/profile.php?id=61584579116180';
const FALLBACK_YOUTUBE = 'https://www.youtube.com/channel/UC6vpaVbRh40ftxKIAdtix2A';

const col = (title, links) => ({ title, links });

const FOOTER_COLS = [
  col('Customer Care', [
    { label: 'Track Your Order', href: '/track-order' },
    { label: 'Returns & Refunds', href: '/refund-policy' },
    { label: 'Shipping Policy', href: '/shipping-policy' },
    { label: 'Contact Us', href: '/contact' },
    { label: 'FAQs', href: '/faq' },
    { label: 'WhatsApp Support', whatsapp: true, external: true },
  ]),
  col('Know More', [
    { label: 'About Us', href: '/about' },
    { label: 'Blog', href: '/blog' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms & Conditions', href: '/terms' },
    { label: 'Cookie Policy', href: '/cookie-policy' },
  ]),
];

export default function Footer() {
  const { logoUrl } = useCart();
  const settings = usePublicSettings();
  const whatsappNumber = (settings.whatsapp_number || FALLBACK_WHATSAPP).replace(/\D/g, '');
  const instagramUrl = settings.instagram_url || FALLBACK_INSTAGRAM;
  const facebookUrl = settings.facebook_url || FALLBACK_FACEBOOK;
  const youtubeUrl = settings.youtube_url || FALLBACK_YOUTUBE;
  return (
    <footer className="bg-brand-dark text-gray-300 mt-16 pb-28 md:pb-0">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">

          {/* Brand column */}
          <div className="col-span-2 lg:col-span-1 flex flex-col items-center text-center sm:items-start sm:text-left">
            <div className="mb-4">
              <img src={cldImg(logoUrl, 300) || '/logo.png'} alt="TheYellove" className="h-28 w-auto object-contain" />
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-5">
              Discover products you'll love to gift and keep.
            </p>
            <div className="flex gap-3 justify-center sm:justify-start">
              <a href={instagramUrl} target="_blank" rel="noreferrer"
                className="w-9 h-9 bg-white/10 hover:bg-pink-500 hover:text-white rounded-lg flex items-center justify-center transition-colors text-gray-300">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href={facebookUrl} target="_blank" rel="noreferrer"
                className="w-9 h-9 bg-white/10 hover:bg-blue-600 hover:text-white rounded-lg flex items-center justify-center transition-colors text-gray-300">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href={youtubeUrl} target="_blank" rel="noreferrer"
                className="w-9 h-9 bg-white/10 hover:bg-red-600 hover:text-white rounded-lg flex items-center justify-center transition-colors text-gray-300">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/></svg>
              </a>
              <a href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hi! I would like to enquire about a product I am interested in.")}`} target="_blank" rel="noreferrer"
                className="w-9 h-9 bg-white/10 hover:bg-green-500 hover:text-white rounded-lg flex items-center justify-center transition-colors text-gray-300">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
            </div>
          </div>

          {/* Contact column */}
          <div className="col-span-2 lg:col-span-1 flex flex-col items-center text-center sm:items-start sm:text-left">
            <h4 className="text-white font-semibold text-sm mb-4">Contact</h4>
            <ul className="space-y-2.5">
              <li className="text-sm text-gray-400">TheYellove</li>
              <li>
                <a href="mailto:Support@theyellove.in" className="text-sm text-gray-400 hover:text-brand-yellow transition-colors">
                  Support@theyellove.in
                </a>
              </li>
              <li>
                <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noreferrer" className="text-sm text-gray-400 hover:text-brand-yellow transition-colors">
                  WhatsApp: {whatsappNumber.replace(/^91/, '')}
                </a>
              </li>
            </ul>
          </div>

          {/* Link columns */}
          {FOOTER_COLS.map(col => (
            <div key={col.title} className="flex flex-col items-center text-center sm:items-start sm:text-left">
              <h4 className="text-white font-semibold text-sm mb-4">{col.title}</h4>
              <ul className="space-y-2.5">
                {col.links.map(link => (
                  <li key={link.label}>
                    {link.external ? (
                      <a href={link.whatsapp ? `https://wa.me/${whatsappNumber}` : link.href} target="_blank" rel="noreferrer"
                        className="text-sm text-gray-400 hover:text-brand-yellow transition-colors">
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.href} className="text-sm text-gray-400 hover:text-brand-yellow transition-colors">
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
          <p className="text-xs text-gray-500 text-center">© {new Date().getFullYear()} TheYellove. All rights reserved.</p>
          <div className="flex flex-col items-center gap-1 sm:flex-row sm:gap-3">
            <span className="text-xs text-gray-500">Secure payments via</span>
            <div className="flex gap-2 items-center">
              <span className="bg-white/10 text-white text-xs px-2 py-1 rounded font-medium">UPI</span>
              <span className="bg-white/10 text-white text-xs px-2 py-1 rounded font-medium">Razorpay</span>
              <span className="bg-white/10 text-white text-xs px-2 py-1 rounded font-medium">Cards</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
